'use client';

import { CheckCircle, Error, Info } from '@mui/icons-material';
import type { AlertProps, SxProps, Theme } from '@mui/material';
import { Alert, AlertTitle, Box, useTheme } from '@mui/material';
import type { CustomContentProps } from 'notistack';
import React, { useCallback } from 'react';
import { ToastTimer } from './toast-timer';
interface ToastProps extends CustomContentProps {
  action: React.ReactNode;
  title: string;
  message: React.ReactNode;
  alertProps: AlertProps;
}
export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(function Toast(props, ref) {
  // `style` carries the enter and exit transition's styles, e.g. a fade's
  // opacity, so it goes on the root alongside any of the alert's own.
  const {
    autoHideDuration,
    message,
    alertProps,
    style
  } = props;
  const ICON_MAP = {
    error: <Error />,
    info: <Info />,
    success: <CheckCircle />,
    warning: <Error />
  } as const;
  const COLOR_MAP = {
    error: 'error',
    info: 'secondary',
    success: 'success',
    warning: 'warning'
  } as const;

  // Ensure severity is always a valid key
  const severity = alertProps?.severity as string | undefined;
  const icon = severity && severity in ICON_MAP ? ICON_MAP[severity as keyof typeof ICON_MAP] : ICON_MAP.info;
  const color = severity && severity in COLOR_MAP ? COLOR_MAP[severity as keyof typeof COLOR_MAP] : 'info';
  // The theme decides the style for every toast; a theme that doesn't set
  // one (or isn't from `generateTheme`) gets the soft toast.
  const variant = useTheme().toastStyle === 'hard' ? 'filled' : 'standard';
  const styles: SxProps<Theme> = useCallback((theme: Theme) => ({
    ...(typeof alertProps.sx === 'function' ? alertProps.sx(theme) as any : alertProps.sx),
    display: 'flex',
    flexWrap: 'nowrap',
    flexGrow: {
      xs: 1,
      sm: 'initial'
    },
    minWidth: {
      sm: '288px'
    },
    padding: '8px 8px 8px 16px',
    // The soft toast lays the translucent container tint over the opaque
    // paper surface, above Paper's dark-mode elevation overlay. A theme
    // not built by `generateTheme` has no tint and shows plain paper.
    backgroundColor: variant === 'filled' ? `${color}.main` : 'background.paper',
    ...(variant !== 'filled' && theme.palette.containerBackground && {
      backgroundImage: `linear-gradient(${theme.palette.containerBackground.main}, ${theme.palette.containerBackground.main}), var(--Paper-overlay, none)`
    }),
    position: 'relative',
    overflow: 'hidden',
    // Override alertContent color from theme styleOverrides
    '&.MuiAlert-root': {
      color: variant === 'filled' ? `${color}.contrastText` : 'text.primary'
    },
    '.MuiAlert-action svg': {
      color: 'action.active'
    }
  }), [alertProps]);
  return <Alert ref={ref} role="alert" severity="success" icon={false} elevation={12} action={alertProps.action} {...alertProps} variant={variant} style={{
    ...alertProps.style,
    ...style
  }} sx={styles}>
        <AlertTitle sx={{
      display: 'flex',
      alignItems: 'center',
      marginBottom: '2px'
    }}>
          {/* Icon */}
          {icon && <Box sx={{
        mr: 1.5,
        display: 'flex',
        alignItems: 'center',
        '> svg': {
          height: 22,
          width: 22,
          color: variant === 'filled' ? `${color}.contrastText` : `${color}.main`
        }
      }}>
              {icon}
            </Box>}
          {/* Title */}
          {alertProps.title}
        </AlertTitle>
        {/* Message */}
        <Box sx={{
      wordBreak: 'break-word',
      ...(variant === 'filled' && {
        fontWeight: 500
      })
    }}>
          {message}
        </Box>
        {/* Timer */}
        {autoHideDuration ? <ToastTimer duration={autoHideDuration} color={variant === 'filled' ? `${color}.dark` : `${color}.light`} /> : null}
      </Alert>;
});